import type { CSSProperties, ReactNode } from 'react';

/**
 * Inline stroke-SVG icon set for Harmony AP.
 *
 * Zero dependencies, consistent rendering across devices (replaces emoji),
 * matching the hand-rolled SVG approach of the Harmony CMA apps. All icons
 * inherit `currentColor` and use round stroke joins.
 */
export type IconName =
  | 'schedule'
  | 'attendance'
  | 'notes'
  | 'compliance'
  | 'home'
  | 'clients'
  | 'providers'
  | 'clock'
  | 'upload'
  | 'check'
  | 'alert'
  | 'doc'
  | 'shield'
  | 'shield-check'
  | 'chevron-right'
  | 'pin'
  | 'camera'
  | 'id-card'
  | 'car'
  | 'first-aid'
  | 'graduation'
  | 'briefcase'
  | 'badge';

const PATHS: Record<IconName, ReactNode> = {
  schedule: (
    <>
      <rect x="3" y="4" width="18" height="18" rx="3" />
      <path d="M3 9h18M8 2v4M16 2v4" />
    </>
  ),
  attendance: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  notes: (
    <>
      <path d="M4 4h16v12H7l-3 3Z" />
      <path d="M8 9h8M8 12h5" />
    </>
  ),
  compliance: (
    <>
      <path d="M12 2 4 5v6c0 5 3.4 7.7 8 9 4.6-1.3 8-4 8-9V5Z" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  shield: <path d="M12 2 4 5v6c0 5 3.4 7.7 8 9 4.6-1.3 8-4 8-9V5Z" />,
  'shield-check': (
    <>
      <path d="M12 2 4 5v6c0 5 3.4 7.7 8 9 4.6-1.3 8-4 8-9V5Z" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  home: (
    <>
      <path d="M3 11l9-8 9 8" />
      <path d="M5 10v10h14V10" />
    </>
  ),
  clients: (
    <>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 20a6 6 0 0 1 12 0" />
      <path d="M16 5.6a3 3 0 0 1 0 5.8" />
      <path d="M21 20a6 6 0 0 0-4-5.6" />
    </>
  ),
  providers: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="3" />
      <circle cx="9" cy="11" r="2" />
      <path d="M6 16c.5-1.5 1.6-2.2 3-2.2s2.5.7 3 2.2" />
      <path d="M14.5 10h4M14.5 13h4" />
    </>
  ),
  upload: (
    <>
      <path d="M12 16V4" />
      <path d="m6 10 6-6 6 6" />
      <path d="M4 20h16" />
    </>
  ),
  check: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m8.5 12 2.5 2.5 4.5-5" />
    </>
  ),
  alert: (
    <>
      <path d="M12 3 2 20h20Z" />
      <path d="M12 10v4M12 17h.01" />
    </>
  ),
  doc: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
      <path d="M14 2v6h6M9 14h6M9 17h4" />
    </>
  ),
  'chevron-right': <path d="m9 6 6 6-6 6" />,
  pin: (
    <>
      <path d="M12 21s7-5.7 7-11a7 7 0 1 0-14 0c0 5.3 7 11 7 11Z" />
      <circle cx="12" cy="10" r="2.5" />
    </>
  ),
  camera: (
    <>
      <path d="M3 7h3l2-2.5h8L18 7h3v13H3Z" />
      <circle cx="12" cy="13" r="3.5" />
    </>
  ),
  'id-card': (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <circle cx="8.5" cy="11" r="2.2" />
      <path d="M5 16c.4-1.4 1.6-2.1 3.5-2.1S11.6 14.6 12 16" />
      <path d="M14.5 9.5h4M14.5 12.5h4M14.5 15.5h2.5" />
    </>
  ),
  car: (
    <>
      <path d="M3 13l2-5a2 2 0 0 1 1.9-1.3h10.2A2 2 0 0 1 19 8l2 5" />
      <path d="M3 13h18v4a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1v-1H6v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1Z" />
      <path d="M6.5 16h.01M17.5 16h.01" />
    </>
  ),
  'first-aid': (
    <>
      <rect x="3" y="6" width="18" height="14" rx="2.5" />
      <path d="M8 6V4.5A1.5 1.5 0 0 1 9.5 3h5A1.5 1.5 0 0 1 16 4.5V6" />
      <path d="M12 10v6M9 13h6" />
    </>
  ),
  graduation: (
    <>
      <path d="M12 4 2 9l10 5 10-5Z" />
      <path d="M6 11v4.5c0 1.4 2.7 2.5 6 2.5s6-1.1 6-2.5V11" />
    </>
  ),
  briefcase: (
    <>
      <rect x="3" y="7" width="18" height="13" rx="2.5" />
      <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M3 12h18" />
    </>
  ),
  badge: (
    <>
      <path d="M12 2 4 5v6c0 5 3.4 7.7 8 9 4.6-1.3 8-4 8-9V5Z" />
      <circle cx="12" cy="10" r="2.4" />
      <path d="M9 16.5c.6-1.6 1.8-2.5 3-2.5s2.4.9 3 2.5" />
    </>
  ),
};

interface IconProps {
  name: IconName;
  size?: number;
  strokeWidth?: number;
  className?: string;
  style?: CSSProperties;
}

export default function Icon({
  name,
  size = 24,
  strokeWidth = 2,
  className,
  style,
}: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name] ?? null}
    </svg>
  );
}
